/**
 * AI SDK 6 Agent System
 * 
 * This module provides agent functionality using the AI SDK's ToolLoopAgent.
 * Agents are autonomous AI systems that can use tools, make decisions,
 * and complete multi-step tasks.
 * 
 * Key Features:
 * - Reusable agent configurations
 * - Multi-step tool calling loops
 * - Streaming and non-streaming modes
 * - Structured output parsing
 * - Integration with MCP tools
 * 
 * @see https://ai-sdk.dev/docs/agents/building-agents
 */

import { ToolLoopAgent, stepCountIs, type Tool, Output } from 'ai';
import { z } from 'zod';
import type { AIApiKeys } from './ai';
import { getLanguageModel } from './ai-sdk';
import { availableTools } from './ai-tools';
import type { ChatModel } from '../constant';

// =============================================================================
// TYPES
// =============================================================================

/** Internal agent definition for the AI SDK agent system */
export interface AgentDefinition {
    /** Unique identifier for the agent */
    id: string;
    /** Display name */
    name: string;
    /** System instructions that define agent behavior */
    instructions: string;
    /** Tools available to the agent */
    tools?: Record<string, Tool>;
    /** Maximum steps before stopping (default: 10) */
    maxSteps?: number;
    /** Tool choice strategy */
    toolChoice?: 'auto' | 'none' | 'required';
    /** Temperature for generation */
    temperature?: number;
    /** Maximum output tokens */
    maxOutputTokens?: number;
}

export interface AgentResult {
    /** Final text response */
    text: string;
    /** All steps the agent took */
    steps: AgentStep[];
    /** Token usage */
    usage?: {
        promptTokens: number;
        completionTokens: number;
        totalTokens: number;
    };
}

export interface AgentStep {
    /** Step number */
    stepNumber: number;
    /** Generated text in this step (if any) */
    text?: string;
    /** Tool calls made in this step */
    toolCalls?: {
        toolName: string;
        input: unknown;
        output: unknown;
    }[];
    /** Reason this step finished */
    finishReason: string;
}

export interface AgentStreamCallbacks {
    /** Called when text is generated */
    onText?: (text: string) => void;
    /** Called when a tool is about to be executed */
    onToolCall?: (toolName: string, input: unknown) => void;
    /** Called when a tool returns a result */
    onToolResult?: (toolName: string, result: unknown) => void;
    /** Called when a step completes */
    onStepFinish?: (step: AgentStep) => void;
    /** Called when the agent finishes */
    onFinish?: (result: AgentResult) => void;
}

// =============================================================================
// AGENT FACTORY
// =============================================================================

/**
 * Create a reusable AI agent
 * 
 * @example
 * const agent = createAgent({
 *   id: 'research-assistant',
 *   name: 'Research Assistant',
 *   instructions: 'You are a helpful research assistant.',
 *   tools: { webSearch: webSearchTool },
 *   maxSteps: 10,
 * }, 'gpt-4o', apiKeys);
 * 
 * const result = await agent.generate({ prompt: 'Research AI trends' });
 */
export function createAgent(
    config: AgentDefinition,
    modelId: ChatModel,
    apiKeys: AIApiKeys
): ToolLoopAgent {
    const model = getLanguageModel(modelId, apiKeys);

    return new ToolLoopAgent({
        model,
        instructions: config.instructions,
        tools: config.tools,
        toolChoice: config.toolChoice ?? 'auto',
        stopWhen: stepCountIs(config.maxSteps ?? 10),
        temperature: config.temperature,
        maxOutputTokens: config.maxOutputTokens,
    });
}

/**
 * Create an agent with structured output
 * 
 * @example
 * const analysisSchema = z.object({
 *   sentiment: z.enum(['positive', 'negative', 'neutral']),
 *   summary: z.string(),
 *   keyPoints: z.array(z.string()),
 * });
 * 
 * const agent = createAgentWithOutput({
 *   id: 'sentiment-analyzer',
 *   name: 'Sentiment Analyzer',
 *   instructions: 'Analyze the sentiment of text.',
 * }, analysisSchema, 'gpt-4o', apiKeys);
 * 
 * const result = await agent.generate({ prompt: 'Analyze: Great product!' });
 * console.log(result.output); // { sentiment: 'positive', summary: '...', keyPoints: [...] }
 */
export function createAgentWithOutput<T extends z.ZodType>(
    config: AgentDefinition,
    outputSchema: T,
    modelId: ChatModel,
    apiKeys: AIApiKeys
): ToolLoopAgent {
    const model = getLanguageModel(modelId, apiKeys);

    return new ToolLoopAgent({
        model,
        instructions: config.instructions,
        tools: config.tools,
        toolChoice: config.toolChoice ?? 'auto',
        stopWhen: stepCountIs(config.maxSteps ?? 10),
        temperature: config.temperature,
        maxOutputTokens: config.maxOutputTokens,
        output: Output.object({ schema: outputSchema }),
    });
}

// =============================================================================
// AGENT EXECUTION
// =============================================================================

/**
 * Run an agent with a prompt and get the result
 * 
 * @example
 * const result = await runAgent({
 *   config: {
 *     id: 'helper',
 *     name: 'Helper',
 *     instructions: 'You are helpful.',
 *   },
 *   modelId: 'gpt-4o',
 *   apiKeys,
 *   prompt: 'What is the weather?',
 *   tools: { weather: weatherTool },
 * });
 */
export async function runAgent(options: {
    config: AgentDefinition;
    modelId: ChatModel;
    apiKeys: AIApiKeys;
    prompt: string;
    tools?: Record<string, Tool>;
    signal?: AbortSignal;
}): Promise<AgentResult> {
    const { config, modelId, apiKeys, prompt, tools, signal } = options;

    const agent = createAgent(
        { ...config, tools: tools ?? config.tools },
        modelId,
        apiKeys
    );

    const result = await agent.generate({
        prompt,
        abortSignal: signal,
    });

    // Convert steps to our format
    const steps: AgentStep[] = result.steps.map((step, index) => ({
        stepNumber: index + 1,
        text: step.text || undefined,
        toolCalls: step.toolCalls?.map(tc => ({
            toolName: tc.toolName,
            input: tc.input,
            output: step.toolResults?.find(tr => tr.toolCallId === tc.toolCallId)?.output,
        })),
        finishReason: step.finishReason,
    }));

    return {
        text: result.text,
        steps,
        usage: result.usage ? {
            promptTokens: result.usage.inputTokens ?? 0,
            completionTokens: result.usage.outputTokens ?? 0,
            totalTokens: result.usage.totalTokens ?? 0,
        } : undefined,
    };
}

/**
 * Run an agent with streaming output
 * 
 * Note: This is a simplified streaming implementation that collects 
 * results after streaming completes. For real-time streaming updates,
 * use the agent's stream() method directly.
 * 
 * @example
 * await streamAgent({
 *   config: { id: 'writer', name: 'Writer', instructions: 'Write stories.' },
 *   modelId: 'claude-sonnet-4-5',
 *   apiKeys,
 *   prompt: 'Write a short story',
 *   callbacks: {
 *     onText: (text) => process.stdout.write(text),
 *     onFinish: (result) => console.log('Done!', result.steps.length, 'steps'),
 *   },
 * });
 */
export async function streamAgent(options: {
    config: AgentDefinition;
    modelId: ChatModel;
    apiKeys: AIApiKeys;
    prompt: string;
    tools?: Record<string, Tool>;
    callbacks?: AgentStreamCallbacks;
    signal?: AbortSignal;
}): Promise<AgentResult> {
    const { config, modelId, apiKeys, prompt, tools, callbacks, signal } = options;

    const agent = createAgent(
        { ...config, tools: tools ?? config.tools },
        modelId,
        apiKeys
    );

    // For streaming, we use generate() which gives us full access to results
    // The AI SDK's ToolLoopAgent.stream() returns a complex async type
    // Use agent.generate() for now and simulate streaming via onStepFinish
    const result = await agent.generate({
        prompt,
        abortSignal: signal,
    });

    // Convert steps
    const agentSteps: AgentStep[] = result.steps.map((step, index) => {
        const stepResult: AgentStep = {
            stepNumber: index + 1,
            text: step.text || undefined,
            toolCalls: step.toolCalls?.map((tc) => ({
                toolName: tc.toolName,
                input: tc.input,
                output: step.toolResults?.find((tr) => tr.toolCallId === tc.toolCallId)?.output,
            })),
            finishReason: step.finishReason,
        };

        // Call onText for text content
        if (step.text) {
            callbacks?.onText?.(step.text);
        }

        // Call tool callbacks
        step.toolCalls?.forEach((tc) => {
            callbacks?.onToolCall?.(tc.toolName, tc.input);
            const tr = step.toolResults?.find((r) => r.toolCallId === tc.toolCallId);
            if (tr) {
                callbacks?.onToolResult?.(tc.toolName, tr.output);
            }
        });

        callbacks?.onStepFinish?.(stepResult);

        return stepResult;
    });

    const agentResult: AgentResult = {
        text: result.text,
        steps: agentSteps,
        usage: result.usage ? {
            promptTokens: result.usage.inputTokens ?? 0,
            completionTokens: result.usage.outputTokens ?? 0,
            totalTokens: result.usage.totalTokens ?? 0,
        } : undefined,
    };

    callbacks?.onFinish?.(agentResult);

    return agentResult;
}

// =============================================================================
// PREDEFINED AGENTS
// =============================================================================

/**
 * Predefined agent configurations for common use cases
 */
export const AGENT_CONFIGS = {
    /**
     * General purpose assistant
     */
    assistant: (): AgentDefinition => ({
        id: 'assistant',
        name: 'Assistant',
        instructions: `You are a helpful AI assistant. You are friendly, concise, and accurate.
When you don't know something, you say so. When asked to perform tasks, you do your best
to complete them efficiently.`,
        maxSteps: 10,
    }),

    /**
     * Research assistant with web search capabilities
     */
    researcher: (): AgentDefinition => ({
        id: 'researcher',
        name: 'Research Assistant',
        instructions: `You are an expert research assistant. Your job is to help users find and analyze information.

When researching:
1. Start with a broad search to understand the topic
2. Drill down into specific details as needed
3. Cross-reference multiple sources when possible
4. Cite your sources and provide context
5. Summarize findings clearly

Always be thorough but concise.`,
        tools: {
            webSearch: availableTools.webSearch,
            webScraper: availableTools.webScraper,
        },
        maxSteps: 15,
    }),

    /**
     * Code assistant for programming tasks
     */
    coder: (): AgentDefinition => ({
        id: 'coder',
        name: 'Code Assistant',
        instructions: `You are an expert software engineer and code assistant.

When helping with code:
1. Write clean, well-documented code
2. Follow best practices for the language/framework
3. Explain your implementation choices
4. Consider edge cases and error handling
5. Suggest tests when appropriate

Use the calculator for any computations.`,
        tools: {
            calculator: availableTools.calculator,
            dateTime: availableTools.dateTime,
        },
        maxSteps: 10,
    }),

    /**
     * Data analyst for working with data
     */
    analyst: (): AgentDefinition => ({
        id: 'analyst',
        name: 'Data Analyst',
        instructions: `You are a data analyst with expertise in statistics and data interpretation.

When analyzing data:
1. First understand the data structure and context
2. Perform appropriate calculations
3. Look for patterns and trends
4. Provide clear visualizations when helpful
5. Draw actionable conclusions

Always validate your calculations.`,
        tools: {
            calculator: availableTools.calculator,
            dateTime: availableTools.dateTime,
        },
        maxSteps: 20,
    }),

    /**
     * Creative writer for content generation
     */
    writer: (): AgentDefinition => ({
        id: 'writer',
        name: 'Creative Writer',
        instructions: `You are a creative writer with expertise in various writing styles.

Your strengths:
- Engaging storytelling
- Clear technical writing
- Persuasive copy
- Thoughtful essays

Adapt your style to the request. Ask clarifying questions if the request is ambiguous.`,
        temperature: 0.8,
        maxSteps: 5,
    }),
};

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Quick run with the default assistant agent
 * 
 * @example
 * const result = await quickAgent('gpt-4o', apiKeys, 'Hello, how are you?');
 */
export async function quickAgent(
    modelId: ChatModel,
    apiKeys: AIApiKeys,
    prompt: string,
    tools?: Record<string, Tool>
): Promise<AgentResult> {
    return runAgent({
        config: AGENT_CONFIGS.assistant(),
        modelId,
        apiKeys,
        prompt,
        tools,
    });
}

/**
 * Run the researcher agent with web search
 * 
 * @example
 * const result = await researchAgent('gpt-4o', apiKeys, 'Latest AI developments');
 */
export async function researchAgent(
    modelId: ChatModel,
    apiKeys: AIApiKeys,
    prompt: string
): Promise<AgentResult> {
    return runAgent({
        config: AGENT_CONFIGS.researcher(),
        modelId,
        apiKeys,
        prompt,
    });
}

/**
 * Run the coder agent for programming tasks
 * 
 * @example
 * const result = await coderAgent('claude-sonnet-4-5', apiKeys, 'Write a binary search');
 */
export async function coderAgent(
    modelId: ChatModel,
    apiKeys: AIApiKeys,
    prompt: string
): Promise<AgentResult> {
    return runAgent({
        config: AGENT_CONFIGS.coder(),
        modelId,
        apiKeys,
        prompt,
    });
}
